import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

import { setLoading } from 'redux/store/common/slice'
import {
  IRFPCCOptions,
  IRFPSurveyCreateRequestPayload,
  IRFPSurveyPayload,
  IRFPVendorCCPayload,
  RFPHospitalUserVoteResponseData,
  TRFPSubmitSurveyVotingRequestData
} from 'features/RFP/RFPSurvey/types'
import { useRFPDetailsContext } from 'features/RFP/RFPDetails/Providers/RFPDetailsContextProvider'
import { useDispatch, useSelector } from 'react-redux'
import {
  createOrUpdateSurveyRequestAsync,
  getRfpVendorCCListRequestAsync,
  getRfpVendorListRequestAsync,
  getSurveyRequestAsync,
  sendSurveyRequestAsync,
  submitSurveyVotingRequestAsync,
  getHospitalUserVoteRequestAsync,
  editSurveyVotingRequestAsync,
  getUserOptionsForSurvey
} from 'features/RFP/RFPSurvey/api'
import { useRFPDetailsPopupContext } from 'features/RFP/RFPDetails/Providers/RFPDetailsPopupProvider'
import { notification } from 'components/Notification'
import { NOTIFICATIONS, VALIDATION_MESSAGES } from 'constants/txt'
import { getUser } from 'redux/store/user/getters'
import { RFP_STATUSES } from 'features/RFP/constants'
import { ROLES } from '../../../Permission'
import { getFullName } from 'helper/common'

type ContextProps = {
  state: {
    vendors: IRFPVendorCCPayload[]
    contractCategories: IRFPCCOptions
    selectedVendors: IRFPVendorCCPayload[]
    selectedVendorValues: Partial<IRFPSurveyCreateRequestPayload>[]
    isSurveyCreated: boolean
    isSurveySubmitted: boolean
    createdSurveyData: IRFPSurveyPayload | null
    surveyResponse: RFPHospitalUserVoteResponseData | null
    allUserOptions: IRFPVendorCCPayload[]
  }
  actions: {
    createSurveyAsync: (vendorValues: IRFPSurveyCreateRequestPayload[]) => void
    submitSurveyVotingAsync: (
      submitSurveyVotingData: RFPHospitalUserVoteResponseData
    ) => void
    sendSurveyAsync: VoidFunction
    getRfpAcceptedVendorListAsync: () => Promise<void>
    getRfpVendorCCListAsync: (
      id: string,
      type?: 'vendor' | 'user'
    ) => Promise<void>
    getHospitalUserVoteAsync: () => Promise<void>
  }
}

const RFPSurveyContext = createContext<ContextProps>({
  state: {} as ContextProps['state'],
  actions: {} as ContextProps['actions']
})

const RFPSurveyContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useDispatch()

  const rfpDetailsContext = useRFPDetailsContext()
  const { createSurveyPopup, submitSurveyPopup } = useRFPDetailsPopupContext()
  const user = useSelector(getUser)

  const [vendors, _setVendors] = useState<IRFPVendorCCPayload[]>([])
  const [contractCategories, _setContractCategories] = useState<IRFPCCOptions>(
    {}
  )
  const [allUserOptions, setAllUserOptions] = useState<IRFPVendorCCPayload[]>(
    []
  )
  const [
    clinicalCoordinatorCategoryNames,
    setClinicalCoordinatorCategoryNames
  ] = useState<string[]>([])
  const [selectedVendorValues, _setSelectedVendorValues] = useState<
    Partial<IRFPSurveyCreateRequestPayload>[]
  >([])
  const [createdSurveyData, _setCreatedSurveyData] =
    useState<IRFPSurveyPayload | null>(null)
  const [surveyResponse, _setSurveyResponse] =
    useState<RFPHospitalUserVoteResponseData | null>(null)

  const isNotHospitalUserRole =
    user?.role !== ROLES.HOSPITAL_USER &&
    user?.role !== ROLES.COMMONS_STAKEHOLDER

  const selectedVendors = useMemo(
    () =>
      vendors
        ? vendors.filter((vendor) =>
            selectedVendorValues
              .map((record) => record.vendor)
              .includes(vendor.id)
          )
        : [],
    [selectedVendorValues, vendors]
  )

  const isSurveyCreated = useMemo(
    () => selectedVendors.length > 0 || !!surveyResponse?.vote.length,
    [selectedVendors.length, surveyResponse?.vote.length]
  )

  const isSurveySubmitted = useMemo(
    () => selectedVendors.length > 0 && !!surveyResponse?.vote.length,
    [selectedVendors.length, surveyResponse?.vote]
  )

  const handleVendorsChange = useCallback(
    (vendorValues: Partial<IRFPSurveyCreateRequestPayload>[]) => {
      _setSelectedVendorValues(vendorValues)
    },
    []
  )

  const getRfpAcceptedVendorListAsync = useCallback(async () => {
    if (!rfpDetailsContext.state.data.uuid) {
      throw new Error('RFP ID not provided')
    }

    try {
      dispatch(setLoading(true))

      if (
        user?.role !== ROLES.HOSPITAL_USER &&
        user?.role !== ROLES.COMMONS_STAKEHOLDER
      ) {
        const rfpVendorListResponse = await getRfpVendorListRequestAsync(
          rfpDetailsContext.state.data.uuid
        )

        if (rfpVendorListResponse.data?.results) {
          _setVendors(rfpVendorListResponse.data?.results)
        }
      }
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch, rfpDetailsContext.state.data.uuid, user?.role])

  const getUserOptions = useCallback(async () => {
    if (!rfpDetailsContext.state.data.uuid) {
      throw new Error('RFP ID not provided')
    }
    try {
      if (isNotHospitalUserRole) {
        const options = await getUserOptionsForSurvey(
          rfpDetailsContext.state.data.uuid
        )
        const normalizedOptions = options.data.results.map((item) => ({
          ...item,
          id: item.uuid,
          value: getFullName(item.first_name, item.last_name),
          role: item.role,
          contract_categories_names: item.contract_categories_names
        }))

        // For clinical coordinator, first fetch categories for current user
        // and keep stakeholders that match at least one category by name.
        if (user?.role === ROLES.CLINICAL_COORDINATOR && user.uuid) {
          const currentUserCategoriesResp =
            await getRfpVendorCCListRequestAsync(
              rfpDetailsContext.state.data.uuid,
              user.uuid,
              'user'
            )

          const currentUserCategoryNames = new Set(
            (currentUserCategoriesResp.data?.results ?? [])
              .map((category) => category.value?.toLowerCase().trim())
              .filter(Boolean)
          )
          setClinicalCoordinatorCategoryNames(
            Array.from(currentUserCategoryNames)
          )

          const filteredOptions = normalizedOptions.filter((stakeholder) => {
            if (stakeholder.role === ROLES.COMMONS_STAKEHOLDER) {
              return false
            }

            const stakeholderCategories =
              stakeholder.contract_categories_names
                ?.split(',')
                .map((name) => name.toLowerCase().trim())
                .filter(Boolean) ?? []

            // Include stakeholders with no categories assigned.
            if (!stakeholderCategories.length) {
              return true
            }

            return stakeholderCategories.some((categoryName) =>
              currentUserCategoryNames.has(categoryName)
            )
          })

          setAllUserOptions(filteredOptions)
          return
        }

        setAllUserOptions(normalizedOptions)
      }
    } catch (e) {
      console.error(e)
    }
  }, [
    isNotHospitalUserRole,
    rfpDetailsContext.state.data.uuid,
    user?.role,
    user?.uuid
  ])

  useEffect(() => {
    if (isSurveyCreated) {
      getUserOptions()
    }
  }, [getUserOptions, isSurveyCreated])

  const getRfpVendorCCListAsync = useCallback(
    async (id: string, type: 'vendor' | 'user' = 'vendor') => {
      if (!rfpDetailsContext.state.data.uuid) {
        throw new Error('RFP ID not provided')
      }
      if (contractCategories[id] || id === 'all') {
        return
      }

      try {
        dispatch(setLoading(true))

        if (
          user?.role !== ROLES.HOSPITAL_USER &&
          user?.role !== ROLES.COMMONS_STAKEHOLDER
        ) {
          const rfpVendorCCListResponse = await getRfpVendorCCListRequestAsync(
            rfpDetailsContext.state.data.uuid,
            id,
            type
          )

          if (rfpVendorCCListResponse.data?.results) {
            const categoriesForUser =
              user?.role === ROLES.CLINICAL_COORDINATOR && type === 'user'
                ? rfpVendorCCListResponse.data.results.filter((category) =>
                    clinicalCoordinatorCategoryNames.includes(
                      category.value?.toLowerCase().trim()
                    )
                  )
                : rfpVendorCCListResponse.data.results

            _setContractCategories((prev) => ({
              ...prev,
              [id]: categoriesForUser
            }))
          }
        }
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setLoading(false))
      }
    },
    [
      contractCategories,
      clinicalCoordinatorCategoryNames,
      dispatch,
      rfpDetailsContext.state.data.uuid,
      user?.role
    ]
  )

  const getRfpSurveyAsync = useCallback(
    async (fillCategories = false) => {
      if (!rfpDetailsContext.state.data.uuid) {
        throw new Error('RFP ID not provided')
      }

      try {
        dispatch(setLoading(true))

        const rfpSurveyResponse = await getSurveyRequestAsync(
          rfpDetailsContext.state.data.uuid
        )

        if (rfpSurveyResponse.data?.survey) {
          _setCreatedSurveyData(rfpSurveyResponse.data)
          _setSelectedVendorValues(
            rfpSurveyResponse.data.survey.map((record) => ({
              vendor: record.vendor.uuid,
              category: record.category.map((cat) => cat.uuid)
            }))
          )
          _setVendors(
            rfpSurveyResponse.data.survey.map((record) => ({
              id: record.vendor.uuid,
              value: record.vendor.name
            }))
          )
          if (fillCategories) {
            _setContractCategories(
              rfpSurveyResponse.data.survey.reduce(
                (contractCategories, record) => {
                  const contractCategoriesList = record.category.map((cat) => ({
                    id: cat.uuid,
                    value: cat.name
                  }))
                  return {
                    ...contractCategories,
                    [record.vendor.uuid]: contractCategoriesList
                  }
                },
                {}
              )
            )
          }
        }
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setLoading(false))
      }
    },
    [dispatch, rfpDetailsContext.state.data.uuid]
  )

  const getHospitalUserVoteAsync = useCallback(async () => {
    if (!rfpDetailsContext.state.data.uuid) {
      throw new Error('RFP ID not provided')
    }

    try {
      dispatch(setLoading(true))

      const response = await getHospitalUserVoteRequestAsync(
        rfpDetailsContext.state.data.uuid
      )

      if (response?.data?.vote?.length > 0) {
        _setSurveyResponse(response.data)
      } else {
        _setSurveyResponse({ vote: [] })
      }
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch, rfpDetailsContext.state.data.uuid])

  const createSurveyAsync = useCallback(
    async (vendorCCValues: IRFPSurveyCreateRequestPayload[]) => {
      if (!rfpDetailsContext.state.data.uuid) {
        throw new Error('RFP ID not provided')
      }

      try {
        dispatch(setLoading(true))

        await createOrUpdateSurveyRequestAsync(
          rfpDetailsContext.state.data.uuid,
          vendorCCValues
        )

        createSurveyPopup.actions.close()
        handleVendorsChange(vendorCCValues)
        notification.success({ message: NOTIFICATIONS.SURVEY_CREATED })
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setLoading(false))
      }
    },
    [
      createSurveyPopup.actions,
      dispatch,
      handleVendorsChange,
      rfpDetailsContext.state.data.uuid
    ]
  )

  const sendSurveyAsync = useCallback(async () => {
    if (!rfpDetailsContext.state.data.uuid) {
      throw new Error('RFP ID not provided')
    }

    try {
      dispatch(setLoading(true))

      await sendSurveyRequestAsync(rfpDetailsContext.state.data.uuid)
      await rfpDetailsContext.actions.handleGetRfpData()
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch, rfpDetailsContext.actions, rfpDetailsContext.state.data.uuid])

  const submitSurveyVotingAsync = useCallback(
    async (submitSurveyVotingData: RFPHospitalUserVoteResponseData) => {
      if (!rfpDetailsContext.state.data.uuid) {
        throw new Error('RFP ID not provided')
      }

      try {
        dispatch(setLoading(true))

        const payloadRequest: TRFPSubmitSurveyVotingRequestData = {
          vote: submitSurveyVotingData.vote.map((data) => ({
            category: data.category.uuid,
            ranked_vendors: data.ranked_vendors.map(
              (vendor) => vendor.vendor.uuid
            )
          }))
        }

        if (isNotHospitalUserRole) {
          const { stakeholderId, categoryId } = submitSurveyPopup.state.payload
          if (stakeholderId) {
            payloadRequest.user = stakeholderId
          } else if (submitSurveyVotingData.vote.length === 1) {
            payloadRequest.category =
              submitSurveyVotingData.vote[0].category.uuid
          } else if (categoryId) {
            payloadRequest.category = categoryId
          }
        }

        const apiRequest =
          isSurveySubmitted && !isNotHospitalUserRole
            ? editSurveyVotingRequestAsync
            : submitSurveyVotingRequestAsync
        await apiRequest(rfpDetailsContext.state.data.uuid, payloadRequest)

        await rfpDetailsContext.actions.handleGetRfpData()

        _setSurveyResponse((prev) => {
          const prevVote = prev?.vote ?? []
          const submittedByCategoryUuid = new Map(
            submitSurveyVotingData.vote.map((item) => [
              item.category.uuid,
              item
            ])
          )

          const mergedVote = prevVote.map(
            (item) => submittedByCategoryUuid.get(item.category.uuid) ?? item
          )

          submitSurveyVotingData.vote.forEach((item) => {
            if (
              !mergedVote.some(
                (existing) => existing.category.uuid === item.category.uuid
              )
            ) {
              mergedVote.push(item)
            }
          })

          return { vote: mergedVote }
        })
        submitSurveyPopup.actions.close()

        notification.success({ message: VALIDATION_MESSAGES.SM0040 })
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setLoading(false))
      }
    },
    [
      dispatch,
      isSurveySubmitted,
      isNotHospitalUserRole,
      rfpDetailsContext.actions,
      rfpDetailsContext.state.data.uuid,
      submitSurveyPopup.actions,
      submitSurveyPopup.state.payload
    ]
  )

  useEffect(() => {
    const status = rfpDetailsContext.state.data.status

    if (
      !user.vendor &&
      (status === RFP_STATUSES.PRICE_FILES_ANALYSIS ||
        status === RFP_STATUSES.VOTING_IN_PROGRESS ||
        status === RFP_STATUSES.VENDORS_SELECTION ||
        status === RFP_STATUSES.ARCHIVED)
    ) {
      getRfpSurveyAsync(
        status === RFP_STATUSES.VENDORS_SELECTION ||
          status === RFP_STATUSES.ARCHIVED
      )
    }
  }, [getRfpSurveyAsync, rfpDetailsContext.state.data.status, user.vendor])

  const state = useMemo(
    () => ({
      vendors,
      contractCategories,
      selectedVendors,
      selectedVendorValues,
      isSurveyCreated,
      isSurveySubmitted,
      createdSurveyData,
      surveyResponse,
      allUserOptions
    }),
    [
      vendors,
      contractCategories,
      selectedVendors,
      selectedVendorValues,
      isSurveyCreated,
      isSurveySubmitted,
      createdSurveyData,
      surveyResponse,
      allUserOptions
    ]
  )

  const actions = useMemo(
    () => ({
      submitSurveyVotingAsync,
      createSurveyAsync,
      sendSurveyAsync,
      getHospitalUserVoteAsync,
      getRfpAcceptedVendorListAsync,
      getRfpVendorCCListAsync
    }),
    [
      sendSurveyAsync,
      createSurveyAsync,
      submitSurveyVotingAsync,
      getHospitalUserVoteAsync,
      getRfpAcceptedVendorListAsync,
      getRfpVendorCCListAsync
    ]
  )
  return (
    <RFPSurveyContext.Provider value={{ state, actions }}>
      {children}
    </RFPSurveyContext.Provider>
  )
}
export const useRFPSurveyContext = () => useContext(RFPSurveyContext)

export default RFPSurveyContextProvider
